import type {BlockedBrowserPermissionsInfoType} from '@pexip/media-components';
import type {Signal} from '@pexip/signal';
import type {
    CursorCaptureConstraint,
    DisplayCaptureSurfaceType,
    IncludeExcludeConstraint,
    MediaDeviceInfoLike,
} from '@pexip/media-control';
import type {
    AudioContentHint,
    VideoContentHint,
    VideoStreamTrackProcessorAPIs,
} from '@pexip/media';
import type {
    Delegate,
    RenderBackend,
    RenderEffects,
} from '@pexip/media-processor';
import type {
    AllowMediaChangeOfDirection,
    ClientCallType,
    ParticipantID,
    RoomID,
    TransferDetails,
} from '@pexip/infinity';

export enum PreflightJoinDetailsToRender {
    BlockedDeviceSelection = 'blocked-device-selection',
    DeviceSelection = 'device-selection',
    Spinner = 'spinner',
}

export interface Idp {
    name: string;
    uuid: string;
    redirectUrl?: string;
    img?: string;
    disablePopupFlow?: boolean;
}

export enum FlowType {
    Express = 'express',
    StepByStep = 'step-by-step',
}

export interface BlockedBrowserPermissionsInfo {
    // vp9 is a webm file format
    vp9: string | undefined;
    mp4: string | undefined;
    browserName: string;
    link?: string;
    helpSteps?: Array<{step: number; text: string}>;
    videoTextOverlaySteps?: Array<{
        step: number;
        text: string;
        duration: number;
    }>;
    permissionInfoType: BlockedBrowserPermissionsInfoType;
}

export interface BrowserDetection<T> {
    onChromeOnMobile: () => T;
    onChromeOnDesktop: () => T;
    onFirefoxOnDesktop: () => T;
    onEdgeOnMobile: () => T;
    onEdgeOnDesktop: () => T;
    onSafariOnIPhone: () => T;
    onSafariOnIpad: () => T;
    onSafariOnMacOs: () => T;
    onOtherBrowser: () => T;
}

export type BrowserType =
    | 'chrome-mobile'
    | 'chrome'
    | 'edge'
    | 'edge-macosx'
    | 'firefox'
    | 'safari-ios'
    | 'safari-ipados'
    | 'safari-macosx'
    | 'other';

export interface PreflightTipsInfo {
    step: number;
    text: React.ReactNode;
}

export enum MeetingFlow {
    Loading = 0,
    AreYouHost = 1,
    EnterPin = 2,
    EnterHostPin = 3,
    EnterExtension = 4,
    Idp = 5,
    InMeeting = 6,
}

export type TestIdValue = string;
export type GetState<T, K extends keyof T> = () => Pick<T, K>;

export enum StepByStepCompletion {
    Uncompleted = 'uncompleted',
    Completed = 'completed',
    Skipped = 'skipped',
    Reviewing = 'reviewing',
}

export type UserMenuStep =
    | 'menu'
    | 'video-and-sound'
    | 'quality'
    | 'meeting-settings'
    | 'layouts'
    | 'accessibility'
    | 'language'
    | 'about'
    | 'stats'
    | 'advanced'
    | 'joining';

export enum ConfirmationModalStates {
    Layout = 'LAYOUT',
    None = 'NONE',
}

export enum BrowserCloseConfirmation {
    Unset = 'unset',
    Enabled = 'enabled',
    Disabled = 'disabled',
}

export type AppContextState = {
    isOnStepByStepJoinScreen: boolean;
    isAriaMenuOpen: boolean;
};

export type SetAppContext = <K extends keyof AppContextState>(
    key: K,
    updater: (prevValue: AppContextState[K]) => AppContextState[K],
) => void;

export type AppContextType = {
    appContext: AppContextState;
    setAppContext: SetAppContext;
};

export interface ImageRecord {
    key: string;
    name: string;
    data: Blob;
}

export interface FeedbackConfig {
    enable?: boolean;
    url?: string;
}

export interface Transcript {
    isFinal: boolean;
    text: string;
    timestamp: number;
    speakers: string[];
}

export enum ParticipantActivities {
    Join = 0,
    Leave = 1,
    Update = 2,
    Transfer = 3,
}

interface BaseParticipantActivity {
    participantId: ParticipantID;
}

export interface JoinActivity extends BaseParticipantActivity {
    activity: ParticipantActivities.Join;
}
export interface LeaveActivity extends BaseParticipantActivity {
    activity: ParticipantActivities.Leave;
}

export interface UpdateActivity extends BaseParticipantActivity {
    activity: ParticipantActivities.Update;
}
export type TransferTarget = TransferDetails['target'];
export interface TransferActivity extends BaseParticipantActivity {
    activity: ParticipantActivities.Transfer;
    destinationType: TransferDetails['target'];
    destination: string;
}

export type ParticipantActivity =
    | JoinActivity
    | LeaveActivity
    | UpdateActivity
    | TransferActivity;

export interface RoomParticipantActivity {
    roomId: RoomID;
    activity: ParticipantActivity;
}

export type GetSignalType<T> = T extends Signal<unknown, infer R> ? R : never;

export type InternalConifg = {
    asd: boolean;
    audioInput: MediaDeviceInfoLike | undefined;
    audioOutput: MediaDeviceInfoLike | undefined;
    lastFallBackMsg: string;
    showLayoutChangeConfirmationModal: boolean;
    showSelfviewHiddenTooltip: boolean;
    stepByStepCompletion: StepByStepCompletion;
    vad: boolean;
    videoInput: MediaDeviceInfoLike | undefined;
};

export type DefaultUserConfig = {
    allowChangeOfMediaDirection: AllowMediaChangeOfDirection;
    alwaysDisplayUIInterfaces: boolean;
    audioContentHint: AudioContentHint;
    autoGainControl: boolean;
    backgroundBlurAmount: number;
    bandwidth: string;
    bgImageUrl: string;
    browserCloseConfirmation: BrowserCloseConfirmation;
    callType: ClientCallType;
    captureAudio: boolean;
    curosrCapture: CursorCaptureConstraint;
    denoise: boolean;
    disableKeyboardShortcuts: boolean;
    displayName: string;
    displaySurface: DisplayCaptureSurfaceType;
    echoCancellation: boolean;
    edgeBlurAmount: number;
    enableBrowserCloseConfirmationByDefault: boolean;
    enforceAudioMute: boolean;
    enforceVideoMute: boolean;
    fecc: boolean;
    foregroundThreshold: number;
    gpuAPI: RenderBackend;
    isAudioInputMuted: boolean;
    isGuest: boolean;
    isUserFacing: boolean;
    isVideoInputMuted: boolean;
    join: boolean;
    maskCombineRatio: number;
    mirrorSelfview: boolean;
    noiseSuppression: boolean;
    preferPresInMix: boolean;
    presoContentHint: VideoContentHint;
    segmentationEffects: RenderEffects;
    showParticipantAuthenticatedState: boolean;
    showSelfView: boolean;
    stopCameraAsMute: boolean;
    surfaceSwitching: IncludeExcludeConstraint;
    userBgImageAssets: string[];
    videoContentHint: VideoContentHint;
    videoProcessingAPI: VideoStreamTrackProcessorAPIs;
    videoProcessingDelegate: Delegate;
};

export type AppConfig = DefaultUserConfig & InternalConifg;

export interface MediaDirection {
    send?: boolean;
    recv?: boolean;
}
export interface CallTypeInput {
    audio?: MediaDirection | boolean;
    video?: MediaDirection | boolean;
    presentation?: MediaDirection | boolean;
}
